import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, addDoc, deleteDoc, doc, serverTimestamp, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from './firebase'; 

export default function TimesheetManager({ tenantId }) {
  // --- CONSTANTS ---
  const ANNUAL_SICK_DAYS = 10;
  const ANNUAL_PERSONAL_DAYS = 3;

  const DAY_TYPES = [
    "Regular Work Day",
    "NTI (Remote Work)",
    "Worked (Non-School Day)",
    "Sick Day",
    "Personal Day",
    "Snow Day (No Work)",
    "Jury Duty",
    "Professional Development",
    "Other / Unpaid"
  ];

  const REQUIRES_TIME = ["Regular Work Day", "NTI (Remote Work)", "Worked (Non-School Day)", "Professional Development"];
  
  // These days do NOT count towards your total contracted days worked
  const NON_COUNTING_TYPES = ["Snow Day (No Work)", "Other / Unpaid"];

  // --- STATE ---
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  
  // Contract Tracking
  const [contractedDays, setContractedDays] = useState(185);
  const [isEditingContract, setIsEditingContract] = useState(false);

  // Balances
  const [sickUsed, setSickUsed] = useState(0);
  const [personalUsed, setPersonalUsed] = useState(0);

  // Form State
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    type: "Regular Work Day",
    timeIn: "07:30",
    timeOut: "15:30",
    notes: ""
  });

  // Calendar State
  const [currentMonthDate, setCurrentMonthDate] = useState(new Date());

  // --- FETCH HISTORY & SETTINGS ---
  const fetchTimesheets = async () => {
    if (!auth.currentUser || !tenantId) return;
    setLoading(true);

    try {
      // 1. Fetch Contracted Days Setting (From the hidden settings doc)
      const settingsRef = doc(db, `districts/${tenantId}/users/${auth.currentUser.uid}/timesheets/_settings`);
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists() && settingsSnap.data().contractedDays) {
        setContractedDays(settingsSnap.data().contractedDays);
      }

      // 2. Fetch Timesheet Logs
      const timesheetRef = collection(db, `districts/${tenantId}/users/${auth.currentUser.uid}/timesheets`);
      const q = query(timesheetRef, orderBy("date", "desc"));
      const snap = await getDocs(q);

      let fetchedEntries = [];
      let tempSickUsed = 0;
      let tempPersonalUsed = 0;

      snap.forEach(doc => {
        if (doc.id === '_settings') return; // Ignore our hidden settings file

        const data = doc.data();
        fetchedEntries.push({ id: doc.id, ...data });
        if (data.type === "Sick Day") tempSickUsed += 1;
        if (data.type === "Personal Day") tempPersonalUsed += 1;
      });

      setEntries(fetchedEntries);
      setSickUsed(tempSickUsed);
      setPersonalUsed(tempPersonalUsed);
    } catch (error) {
      console.error("Error fetching timesheets:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimesheets();
  }, [tenantId]);

  // --- SAVE CONTRACTED SETTINGS ---
  const handleSaveContractedDays = async () => {
    try {
      const settingsRef = doc(db, `districts/${tenantId}/users/${auth.currentUser.uid}/timesheets/_settings`);
      await setDoc(settingsRef, { contractedDays: Number(contractedDays) }, { merge: true });
      setIsEditingContract(false);
    } catch (error) {
      console.error("Failed to update contracted days", error);
      alert(`Could not save settings: ${error.message}`);
    }
  };

  // --- SUBMIT & DELETE ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!auth.currentUser || !tenantId) return;
    setIsSubmitting(true);

    try {
      const timesheetRef = collection(db, `districts/${tenantId}/users/${auth.currentUser.uid}/timesheets`);
      const payload = {
        date: form.date,
        type: form.type,
        notes: form.notes.trim(),
        timestamp: serverTimestamp()
      };

      if (REQUIRES_TIME.includes(form.type)) {
        payload.timeIn = form.timeIn;
        payload.timeOut = form.timeOut;
      } else {
        payload.timeIn = null;
        payload.timeOut = null;
      }

      await addDoc(timesheetRef, payload);
      setForm({ ...form, type: "Regular Work Day", notes: "" });
      
      // Auto-switch calendar to the month of the entry just submitted
      const submittedDate = new Date(form.date + 'T12:00:00');
      setCurrentMonthDate(submittedDate);
      
      fetchTimesheets();
    } catch (error) {
      console.error("Error saving timesheet:", error);
      alert("Failed to save entry. Check permissions.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this timesheet entry?")) return;
    try {
      const docRef = doc(db, `districts/${tenantId}/users/${auth.currentUser.uid}/timesheets`, entryId);
      await deleteDoc(docRef);
      fetchTimesheets();
    } catch (error) {
      console.error("Error deleting entry:", error);
      alert("Failed to delete entry.");
    }
  };

  // --- FORMATTING & CALENDAR LOGIC ---
  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const [hour, minute] = timeStr.split(':');
    let h = parseInt(hour, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${minute} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  const entryMap = entries.reduce((acc, entry) => {
    acc[entry.date] = entry;
    return acc;
  }, {});

  const daysInMonth = new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth(), 1).getDay();

  const handlePrevMonth = () => setCurrentMonthDate(new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentMonthDate(new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth() + 1, 1));

  const getBadgeStyle = (type) => {
    if (type.includes('Sick')) return { bg: '#ffe6e6', text: '#ba0c2f' };
    if (type.includes('Personal')) return { bg: '#e6ffe6', text: '#28a745' };
    if (type.includes('Snow') || type.includes('NTI') || type.includes('PD')) return { bg: '#f0f0f0', text: '#555' };
    return { bg: '#e6f0fa', text: '#003366' }; 
  };

  // --- PROGRESS CALCULATIONS ---
  // THE FIX: We filter out Snow Days and Unpaid days before counting the total
  const validEntries = entries.filter(entry => !NON_COUNTING_TYPES.includes(entry.type));
  const daysCompleted = validEntries.length;
  
  const daysRemaining = Math.max(0, contractedDays - daysCompleted);
  const progressPercentage = Math.min(100, Math.round((daysCompleted / contractedDays) * 100)) || 0;

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>Loading Timesheet Manager...</div>;

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px', fontFamily: 'sans-serif' }}>
      
      <div style={{ borderBottom: '2px solid #003366', paddingBottom: '15px', marginBottom: '25px' }}>
        <h2 style={{ margin: 0, color: '#003366', fontSize: '28px' }}>My Timesheet & Attendance</h2>
        <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>Log hours, record NTI days, and track leave balances.</p>
      </div>

      {/* OVERALL CONTRACT PROGRESS */}
      <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '12px', border: '1px solid #ddd', boxShadow: '0 8px 24px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: '0 0 5px 0', color: '#003366', fontSize: '20px' }}>Contract Progress</h3>
            <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>Watch your required days tick down to zero.</p>
          </div>
          
          <div style={{ textAlign: 'right', backgroundColor: '#f8fafc', padding: '10px 15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', display: 'block' }}>Contracted Days</span>
            {isEditingContract ? (
              <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                <input 
                  type="number" 
                  value={contractedDays} 
                  onChange={(e) => setContractedDays(e.target.value)} 
                  style={{ width: '60px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <button onClick={handleSaveContractedDays} style={{ background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontWeight: 'bold' }}>Save</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end', marginTop: '2px' }}>
                <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#0F172A' }}>{contractedDays}</span>
                <button onClick={() => setIsEditingContract(true)} style={{ background: 'none', border: 'none', color: '#003366', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>Edit</button>
              </div>
            )}
          </div>
        </div>

        {/* PROGRESS BAR */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
            <span style={{ color: '#28a745' }}>{daysCompleted} Logged</span>
            <span style={{ color: progressPercentage === 100 ? '#28a745' : '#ba0c2f' }}>
              {progressPercentage === 100 ? 'Contract Complete! 🎉' : `${daysRemaining} Remaining`}
            </span>
          </div>
          <div style={{ width: '100%', backgroundColor: '#e2e8f0', borderRadius: '10px', height: '12px', overflow: 'hidden' }}>
            <div style={{ 
              width: `${progressPercentage}%`, 
              backgroundColor: progressPercentage === 100 ? '#28a745' : '#003366', 
              height: '100%', 
              transition: 'width 0.5s ease-in-out' 
            }} />
          </div>
        </div>
      </div>

      {/* BALANCE DASHBOARD (SICK & PERSONAL) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', borderLeft: '5px solid #ba0c2f', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px', textTransform: 'uppercase' }}>Sick Days Remaining</h4>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <span style={{ fontSize: '32px', fontWeight: 'bold', color: '#ba0c2f' }}>{ANNUAL_SICK_DAYS - sickUsed}</span>
            <span style={{ fontSize: '14px', color: '#999' }}>/ {ANNUAL_SICK_DAYS} Total</span>
          </div>
        </div>

        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', borderLeft: '5px solid #003366', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px', textTransform: 'uppercase' }}>Personal Days Remaining</h4>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <span style={{ fontSize: '32px', fontWeight: 'bold', color: '#003366' }}>{ANNUAL_PERSONAL_DAYS - personalUsed}</span>
            <span style={{ fontSize: '14px', color: '#999' }}>/ {ANNUAL_PERSONAL_DAYS} Total</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
        
        {/* LEFT COLUMN: THE DATA ENTRY FORM */}
        <div style={{ flex: '1 1 300px', backgroundColor: '#f9f9f9', padding: '25px', borderRadius: '8px', border: '1px solid #eee', height: 'fit-content' }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '18px' }}>+ Log a Day</h3>
          
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', color: '#555', marginBottom: '5px' }}>Date</label>
              <input 
                type="date" required value={form.date} onChange={(e) => setForm({...form, date: e.target.value})}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', color: '#555', marginBottom: '5px' }}>Type of Day</label>
              <select 
                value={form.type} onChange={(e) => setForm({...form, type: e.target.value})}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '2px solid #003366', backgroundColor: '#fff', boxSizing: 'border-box', fontWeight: 'bold', color: '#003366' }}
              >
                {DAY_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>

            {REQUIRES_TIME.includes(form.type) && (
              <div style={{ display: 'flex', gap: '15px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', color: '#555', marginBottom: '5px' }}>Time In</label>
                  <input type="time" required value={form.timeIn} onChange={(e) => setForm({...form, timeIn: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', color: '#555', marginBottom: '5px' }}>Time Out</label>
                  <input type="time" required value={form.timeOut} onChange={(e) => setForm({...form, timeOut: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }} />
                </div>
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', color: '#555', marginBottom: '5px' }}>Notes / Reason (Optional)</label>
              <textarea 
                value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="e.g., Doctor appointment, Sub coverage..."
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box', minHeight: '80px', fontFamily: 'inherit' }}
              />
            </div>

            <button type="submit" disabled={isSubmitting} style={{ padding: '12px', backgroundColor: '#003366', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: isSubmitting ? 'not-allowed' : 'pointer', marginTop: '10px', fontSize: '15px' }}>
              {isSubmitting ? 'Saving...' : 'Submit Timesheet Entry'}
            </button>
          </form>
        </div>

        {/* RIGHT COLUMN: HISTORY LOG & CALENDAR */}
        <div style={{ flex: '2 1 450px' }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>History</h3>
            
            {/* VIEW TOGGLE BUTTONS */}
            <div style={{ display: 'flex', backgroundColor: '#eee', borderRadius: '6px', padding: '4px' }}>
              <button 
                onClick={() => setViewMode('list')}
                style={{ padding: '6px 12px', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', backgroundColor: viewMode === 'list' ? '#fff' : 'transparent', color: viewMode === 'list' ? '#003366' : '#666', boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
              >
                📝 List View
              </button>
              <button 
                onClick={() => setViewMode('calendar')}
                style={{ padding: '6px 12px', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', backgroundColor: viewMode === 'calendar' ? '#fff' : 'transparent', color: viewMode === 'calendar' ? '#003366' : '#666', boxShadow: viewMode === 'calendar' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
              >
                📅 Calendar View
              </button>
            </div>
          </div>
          
          {entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#fff', borderRadius: '8px', border: '2px dashed #ccc', color: '#999' }}>
              No timesheet entries found. Start logging your days!
            </div>
          ) : (
            <>
              {/* --- LIST VIEW --- */}
              {viewMode === 'list' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {entries.map(entry => {
                    const badge = getBadgeStyle(entry.type);
                    return (
                      <div key={entry.id} style={{ backgroundColor: '#fff', padding: '15px 20px', borderRadius: '8px', border: '1px solid #eee', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#333' }}>{formatDate(entry.date)}</span>
                            <span style={{ backgroundColor: badge.bg, color: badge.text, padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>{entry.type}</span>
                          </div>
                          {entry.timeIn && entry.timeOut && (
                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}><span style={{ fontWeight: 'bold' }}>Clocked:</span> {formatTime(entry.timeIn)} - {formatTime(entry.timeOut)}</div>
                          )}
                          {entry.notes && (
                            <div style={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>"{entry.notes}"</div>
                          )}
                        </div>
                        <button onClick={() => handleDelete(entry.id)} style={{ background: 'none', border: 'none', color: '#ba0c2f', cursor: 'pointer', fontSize: '18px', padding: '10px' }}>&times;</button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* --- CALENDAR VIEW --- */}
              {viewMode === 'calendar' && (
                <div style={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  
                  {/* Calendar Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <button onClick={handlePrevMonth} style={{ background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '5px 10px', cursor: 'pointer', fontWeight: 'bold' }}>&lt;</button>
                    <h4 style={{ margin: 0, fontSize: '18px', color: '#003366' }}>
                      {currentMonthDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </h4>
                    <button onClick={handleNextMonth} style={{ background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '5px 10px', cursor: 'pointer', fontWeight: 'bold' }}>&gt;</button>
                  </div>

                  {/* Days of Week */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px', marginBottom: '10px', textAlign: 'center' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} style={{ fontSize: '12px', fontWeight: 'bold', color: '#999', textTransform: 'uppercase' }}>{day}</div>
                    ))}
                  </div>

                  {/* Calendar Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px' }}>
                    {/* Empty slots for start of month */}
                    {Array.from({ length: firstDayOfMonth }).map((_, i) => <div key={`empty-${i}`} />)}
                    
                    {/* Actual Days */}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const dateStr = `${currentMonthDate.getFullYear()}-${String(currentMonthDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      
                      const entry = entryMap[dateStr];
                      const badge = entry ? getBadgeStyle(entry.type) : null;
                      const isToday = dateStr === new Date().toISOString().split('T')[0];

                      return (
                        <div 
                          key={day} 
                          onClick={() => {
                            if(entry) setForm({...form, date: dateStr, type: entry.type});
                            else setForm({...form, date: dateStr});
                          }}
                          style={{ 
                            border: isToday ? '2px solid #003366' : '1px solid #eee', 
                            borderRadius: '6px', 
                            padding: '8px', 
                            minHeight: '60px',
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            backgroundColor: entry ? badge.bg : '#fafafa',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          title={entry ? `${entry.type} ${entry.notes ? '- ' + entry.notes : ''}` : 'Log day'}
                        >
                          <span style={{ fontSize: '14px', fontWeight: 'bold', color: entry ? badge.text : '#555', marginBottom: '5px' }}>{day}</span>
                          
                          {/* Dot Indicator for entries */}
                          {entry && (
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: badge.text, marginTop: 'auto' }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Calendar Legend */}
                  <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '20px', flexWrap: 'wrap' }}>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#666' }}><div style={{ width:'10px', height:'10px', borderRadius:'50%', backgroundColor:'#003366' }}></div> Regular</div>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#666' }}><div style={{ width:'10px', height:'10px', borderRadius:'50%', backgroundColor:'#ba0c2f' }}></div> Sick</div>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#666' }}><div style={{ width:'10px', height:'10px', borderRadius:'50%', backgroundColor:'#28a745' }}></div> Personal</div>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#666' }}><div style={{ width:'10px', height:'10px', borderRadius:'50%', backgroundColor:'#555' }}></div> NTI/Snow</div>
                  </div>

                </div>
              )}
            </>
          )}
        </div>

      </div>
    </div>
  );
}